import { Button, TextField } from "@material-ui/core";

import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Link,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Eye, EyeSlash } from "phosphor-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { createCookie } from "../../Components/authorization";
import { MovieColabLogo } from "../../Components/Utilities/MovieColabLogo";
import signupAndLoginStyles from "./signupAndLoginStyle";

const ResetPasswordPage = () => {
  const classes = signupAndLoginStyles();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const resetCode = query.get("resetcode");
  const email = query.get("email");

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    new_password: "",
    reset_code: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      reset_code: resetCode,
      email: email,
    }));
  }, [resetCode, email]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";

    if (!formData.new_password) newErrors.new_password = "Password is required";
    else if (formData.new_password.length < 6)
      newErrors.new_password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });

    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }

    if (loginError) setLoginError("");
    if (successMessage) setSuccessMessage("");
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setLoginError("");
    setSuccessMessage("");

    try {
      const payload = {
        email: formData.email,
        new_password: formData.new_password,
        reset_code: formData.reset_code,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}reset-password-code/`,
        payload,
      );

      setSuccessMessage(
        "Password reset successful. Please wait logging you in",
      );

      if (res) {
        setTimeout(async () => {
          const loginRes = await axios.post(
            `${process.env.REACT_APP_BASE_URL}login/`,
            {
              email: formData.email,
              password: formData.new_password,
            },
          );
          if (loginRes) {
            createCookie("refresh", res.data.refresh);
            createCookie("access", res.data.access);
            window.location.href = "/";
          }
        }, 300);
      }

      setFormData({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        confirm_password: "",
      });
      setErrors({});
      console.log("Signup success:", res.data);
    } catch (error) {
      console.log(error);

      const apiMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Signup failed. Please try again.";
      setLoginError(apiMessage);
      console.error("Signup error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  return (
    <Box className={classes.loginContainer}>
      {/* Purple Glow */}
      <div className={classes.glow} />

      <Box sx={{ width: "500px", zIndex: 2 }}>
        {/* Logo */}
        <Box className={classes.topLogo}>
          <MovieColabLogo />
          <Typography className={classes.brand}>Movie Colab</Typography>
        </Box>

        {/* Card */}
        <Card className={classes.loginCard}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.headerContainer}>
              <Typography className={classes.welcomeTitle}>
                Reset Password
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#7A7A83",
                  fontSize: "14px",
                  mt: 1,
                }}
              >
                Type your email and new password then hit to the reset button to
                update your password
              </Typography>
            </Box>

            {loginError && (
              <Fade in>
                <Alert
                  severity="error"
                  className={classes.errorAlert}
                  onClose={() => setLoginError("")}
                >
                  {loginError}
                </Alert>
              </Fade>
            )}

            <Box
              component="form"
              onSubmit={handleSignup}
              className={classes.loginForm}
              noValidate
            >
              {/* Password */}
              <div>
                <span className={classes.inputLabel}>Password</span>
                <div
                  className={classes.inputWrapper}
                  style={{ marginTop: "6px" }}
                >
                  <TextField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    value={formData.new_password}
                    onChange={handleInputChange("new_password")}
                    error={!!errors.new_password}
                    helperText={errors.new_password}
                    InputProps={{
                      disableUnderline: true,
                      style: { fontFamily: "Poppins", marginTop: "5px" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <EyeSlash style={{ color: "white" }} />
                            ) : (
                              <Eye style={{ color: "white" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    className={classes.input}
                    placeholder="Your password here"
                  />
                </div>
              </div>
              <div>
                <span className={classes.inputLabel}>Confirm Password</span>
                <div
                  className={classes.inputWrapper}
                  style={{ marginTop: "6px" }}
                >
                  <TextField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    value={formData.confirm_password}
                    onChange={handleInputChange("confirm_password")}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password}
                    InputProps={{
                      disableUnderline: true,
                      style: { fontFamily: "Poppins", marginTop: "5px" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <EyeSlash style={{ color: "white" }} />
                            ) : (
                              <Eye style={{ color: "white" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    className={classes.input}
                    placeholder="Your password here"
                  />
                </div>
              </div>

              {/* Button */}
              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !formData.email ||
                  !formData.new_password ||
                  !formData.reset_code ||
                  formData.new_password !== formData.confirm_password
                }
                fullWidth
                className={classes.primaryButton}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </Box>

            {/* Bottom */}
            <Divider className={classes.divider} />

            <Box
              className={classes.linksContainer}
              sx={{
                justifyContent: "center",
              }}
            >
              {/* <Link href="#" className={classes.smallLink}>
                Forgot Password?
              </Link> */}
              <Link href="/login" className={classes.smallLink}>
                Back to Login
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
